import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DateRangePicker } from '@/components/DateRangePicker';
import type { useDateRange } from '@/hooks/useDateRange';
import type { MarkerKind } from '@/components/MarkedRangeCalendar';

/**
 * Single source of truth for the patient route's "Date range" toolbar.
 *
 * Every tab (Summary, Logs v1/v2/v3, Nutrients, Goals, Reports, Emotions)
 * used to render `<DateRangePicker>` directly with its own wrapper styling
 * — some in a Card, some bare, some with extra buttons sitting beside the
 * picker, some with a heading. The behavior diverged too: only LogsV3 had
 * the "CGM data doesn't overlap this range" hint banner, so on the other
 * pages the glucose chart would silently render empty with no recovery
 * path. This component absorbs all of that:
 *
 *   • Card + optional heading
 *   • The shared `<DateRangePicker>` (URL-synced, smart-default snaps to
 *     last meal). Its built-in Quick Select dropdown — presets + last
 *     meal + per-device CGM windows — comes along for free; do NOT
 *     render a separate `<QuickSelectRange>` here, that's what produced
 *     the duplicate-dropdown bug.
 *   • Optional `actions` slot for page-specific buttons (e.g. "Add meal")
 *
 * The "no CGM readings in this range" hint banner was originally part of
 * this toolbar, but surfacing it on every tab was too noisy on pages
 * unrelated to CGM (Goals, Reports, Emotions, etc). It now lives inside
 * the Summary tab's Glucose Pattern Summary card via the standalone
 * `<CgmNoDataHint>` helper — see PatientFeedColumn.
 *
 * Drop-in replacement for every existing `<DateRangePicker>` usage:
 *
 *   const dr = useDateRange(patientId);
 *   <DateSelectionToolbar patientId={patientId} value={dr} />
 *
 * With actions:
 *
 *   <DateSelectionToolbar
 *     patientId={patientId}
 *     value={dr}
 *     actions={<Button>Add meal</Button>}
 *   />
 *
 * Pass `markers` to forward activity dots into the calendar (Summary uses
 * this for meal + emotion markers).
 */
type DateRangeApi = ReturnType<typeof useDateRange>;

type Props = {
  patientId: number;
  value: DateRangeApi;
  /** Optional buttons rendered on the right of the picker (e.g. "Add meal").
   *  Stack with the picker on narrow viewports. */
  actions?: React.ReactNode;
  /** Calendar markers forwarded to the date picker — used by Summary to
   *  surface meal/emotion dots in the calendar popover. */
  markers?: Partial<Record<MarkerKind, Date[]>>;
  /** Optional title override. Defaults to "Date range" so the heading is
   *  consistent everywhere; pass null to hide the heading entirely (e.g.
   *  when the toolbar lives inside a card that already has its own title). */
  title?: string | null;
  /** Bare = no Card chrome (shared sticky bar under patient tabs). */
  variant?: 'card' | 'bare';
};

export const DateSelectionToolbar = ({
  patientId,
  value: dr,
  actions,
  markers,
  title,
  variant = 'card',
}: Props) => {
  const { t } = useTranslation();
  const headingText = title === undefined ? t('Date range') : title;

  const body = (
    <div className="flex flex-wrap items-center justify-between gap-2">
      {/* DateRangePicker already composes the Quick Select dropdown
          inside itself — render only one here. */}
      <DateRangePicker value={dr} patientId={patientId} markers={markers} />
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );

  if (variant === 'bare') {
    return <div className="py-1">{body}</div>;
  }

  return (
    <Card>
      {headingText !== null && (
        <CardHeader className="px-4 pb-1 pt-3">
          <CardTitle className="text-sm">{headingText}</CardTitle>
        </CardHeader>
      )}
      <CardContent className={headingText !== null ? 'px-4 pb-3 pt-0' : 'p-3'}>{body}</CardContent>
    </Card>
  );
};
